/**
 * MobileDrawer
 * Full-screen mobile navigation. Slides in from the right.
 *
 * Contents (in order):
 *   1. Close row (logo + X)
 *   2. Primary NavLinks (5)   : Home, About, Experience, Work, Blog
 *   3. Control toggles (2)   : Theme, Sound
 *   4. Separator
 *   5. Secondary NavLinks (2): kevin-bot, Contact (IconBox + label; Now/Talks hidden)
 *   6. Language + font size selectors
 *
 * All control toggles use react-router for navigation, NOT scrollIntoView.
 */
import * as React from "react";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import {
  X,
  Moon,
  Sun,
  Globe,
  Type,
  Volume2,
  VolumeX,
  Activity,
  Mic,
  Sparkles,
  Mail,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";
import { useNeuralAudio } from "../../lib/audio";
import { PRIMARY_NAV, SECONDARY_NAV } from "../../config/navigation";
import { Text } from "../atoms/Text";
import { LogoMark } from "./LogoMark";
import { useTheme } from "../../context/ThemeContext";

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SECONDARY_NAV_ICONS: Record<string, LucideIcon> = {
  now: Activity,
  talks: Mic,
  kevinBot: Sparkles,
  contact: Mail,
};

const FONT_SIZES = [
  { id: "small", key: "font_size_small" },
  { id: "default", key: "font_size_default" },
  { id: "large", key: "font_size_large" },
] as const;

type FontSizeId = typeof FONT_SIZES[number]["id"];

const FONT_SIZE_PX: Record<FontSizeId, string> = {
  small: "87.5%",
  default: "100%",
  large: "112.5%",
};

export const MobileDrawer = ({ isOpen, onClose }: MobileDrawerProps) => {
  const { t, i18n } = useTranslation();
  const { play, isMuted, toggleMute } = useNeuralAudio();
  const { theme, toggleTheme } = useTheme();
  const [fontSize, setFontSize] = React.useState<FontSizeId>(() => {
    if (typeof window === "undefined") return "default";
    const stored = window.localStorage.getItem("font-size");
    return stored === "small" || stored === "large" ? stored : "default";
  });

  const applyFontSize = (size: FontSizeId) => {
    if (typeof document === "undefined") return;
    document.documentElement.style.fontSize = FONT_SIZE_PX[size];
    try {
      window.localStorage.setItem("font-size", size);
    } catch {
      /* ignore */
    }
  };

  const handleFontSizeChange = React.useCallback((size: string) => {
    const next = size as FontSizeId;
    setFontSize(next);
    applyFontSize(next);
  }, []);

  const handleLanguageChange = React.useCallback(
    (lng: string) => {
      i18n.changeLanguage(lng);
    },
    [i18n]
  );

  const handleClose = React.useCallback(() => {
    play("system");
    onClose();
  }, [play, onClose]);

  const handleNav = React.useCallback(() => {
    play("beep");
    onClose();
  }, [play, onClose]);

  const drawerVariants: Variants = {
    closed: { opacity: 0, x: "100%", transition: { type: "spring", stiffness: 300, damping: 30 } },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.08,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants: Variants = {
    closed: { opacity: 0, x: 20 },
    open: { opacity: 1, x: 0 },
  };

  const controlButtonClass =
    "flex size-10 items-center justify-center rounded-md border border-border bg-surface text-primary transition-colors hover:bg-surface-elevated";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="closed"
          animate="open"
          exit="closed"
          variants={drawerVariants}
          className="fixed inset-0 z-[200] bg-surface-elevated flex flex-col p-8 overflow-y-auto no-scrollbar"
        >
          {/* Header row */}
          <div className="flex items-center justify-between mb-6">
            <LogoMark href="/" onClick={handleNav} />
            <motion.button
              variants={itemVariants}
              onClick={handleClose}
              aria-label={t("settings.aria_close", { defaultValue: "Close menu" })}
              className="p-3 rounded-full bg-surface border border-border text-primary hover:bg-surface-elevated transition-colors"
            >
              <X size={24} />
            </motion.button>
          </div>

          {/* Primary nav */}
          <nav className="flex flex-col gap-6 mb-6" aria-label="Primary">
            {PRIMARY_NAV.map((link) => (
              <motion.div key={link.id} variants={itemVariants}>
                <NavLink
                  to={link.href}
                  onClick={handleNav}
                  end={link.href === "/"}
                  className={({ isActive: navActive }) =>
                    `text-2xl font-heading font-semibold tracking-tight cursor-pointer transition-colors ${
                      navActive ? "text-accent" : "text-primary hover:text-accent"
                    }`
                  }
                >
                  {t(link.labelKey, { defaultValue: link.id })}
                </NavLink>
              </motion.div>
            ))}
          </nav>

          {/* Controls */}
          <motion.div variants={itemVariants} className="flex items-center gap-2 mb-6">
            <button
              type="button"
              onClick={() => {
                play("toggle", { variant: theme === "dark" ? "on" : "off" });
                toggleTheme();
              }}
              aria-label={t(theme === "dark" ? "settings.theme_light" : "settings.theme_dark", {
                defaultValue: theme === "dark" ? "Switch to light" : "Switch to dark",
              })}
              className={controlButtonClass}
            >
              {theme === "dark" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button
              type="button"
              onClick={() => {
                play("toggle", { variant: isMuted ? "on" : "off" });
                toggleMute();
              }}
              aria-label={t(isMuted ? "settings.sound_on" : "settings.sound_off", {
                defaultValue: isMuted ? "Turn sound on" : "Turn sound off",
              })}
              className={controlButtonClass}
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </motion.div>

          {/* Separator */}
          <motion.div variants={itemVariants} className="h-px w-20 bg-border-subtle mb-6" />

          {/* Secondary nav */}
          <nav className="flex flex-col gap-6" aria-label="More">
            {SECONDARY_NAV.map((link) => {
              const Icon = SECONDARY_NAV_ICONS[link.id];
              return (
                <motion.div key={link.id} variants={itemVariants}>
                  <NavLink
                    to={link.href}
                    onClick={handleNav}
                    className={({ isActive }) =>
                      `group flex items-center gap-4 py-3 transition-colors ${
                        isActive ? "text-accent" : "text-secondary hover:text-primary"
                      }`
                    }
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border-subtle bg-surface-elevated text-secondary transition-colors group-hover:text-primary">
                      <Icon size={18} />
                    </span>
                    <span className="font-heading text-lg font-medium">
                      {t(link.labelKey, { defaultValue: link.id })}
                    </span>
                  </NavLink>
                </motion.div>
              );
            })}
          </nav>

          {/* Footer / Accessibility */}
          <motion.div variants={itemVariants} className="mt-auto space-y-8 pt-8">
            {/* Language toggle */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Globe size={12} className="text-accent" />
                <Text
                  variant="mono"
                  intent="accent"
                  className="text-[10px] uppercase tracking-widest font-black"
                >
                  {t("settings.language", { defaultValue: "Language" })}
                </Text>
              </div>
              <div className="flex gap-1 rounded-xl bg-surface border border-border-subtle p-1 w-fit">
                {[
                  { code: "en", label: "EN" },
                  { code: "es", label: "ES" },
                ].map((lang) => {
                  const active = i18n.language.split("-")[0] === lang.code;
                  return (
                    <button
                      key={lang.code}
                      onClick={() => {
                        play("calibrate");
                        handleLanguageChange(lang.code);
                      }}
                      className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
                        active
                          ? "bg-accent-bg text-accent"
                          : "text-secondary hover:bg-surface-elevated hover:text-primary"
                      }`}
                    >
                      {lang.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Font size */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Type size={12} className="text-accent" />
                <Text
                  variant="mono"
                  intent="accent"
                  className="text-[10px] uppercase tracking-widest font-black"
                >
                  {t("settings.font_size", { defaultValue: "Font size" })}
                </Text>
              </div>
              <div className="flex gap-1 rounded-xl bg-surface border border-border-subtle p-1 w-fit">
                {FONT_SIZES.map((fs) => {
                  const active = fontSize === fs.id;
                  return (
                    <button
                      key={fs.id}
                      onClick={() => {
                        play("calibrate");
                        handleFontSizeChange(fs.id);
                      }}
                      className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
                        active
                          ? "bg-accent-bg text-accent"
                          : "text-secondary hover:bg-surface-elevated hover:text-primary"
                      }`}
                    >
                      {t(`settings.${fs.key}`, { defaultValue: fs.id })}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileDrawer;
